import { z } from 'zod'

import { newRequestId } from '@/api/request-id'
import type { Renewal, RenewReason } from '@/lib/auth/session-source'

/**
 * The page's side of the Flutter bridge, contract v1
 * (mm-embed-2026-10-29/CONTRACT.md). The app opens the chart in a WebView
 * with `?embed=1`, keeps the refresh token itself, and hands the page a
 * 30-minute access token whenever it asks. Page → app goes through the
 * `MMApp` JavaScript channel the app injects; app → page through
 * `window.mmBridge.receive`.
 */

/** The channel Flutter's `addJavaScriptChannel('MMApp', …)` injects. */
interface AppChannel {
  postMessage: (message: string) => void
}

declare global {
  interface Window {
    MMApp?: unknown
    mmBridge?: { receive: (message: unknown) => void }
  }
}

const themeSchema = z.enum(['light', 'dark'])
export type AppTheme = z.infer<typeof themeSchema>

const appMessageSchema = z.union([
  z.object({
    v: z.literal(1),
    type: z.literal('token'),
    id: z.string(),
    access: z.string().min(1),
  }),
  z.object({
    v: z.literal(1),
    type: z.literal('token'),
    id: z.string(),
    error: z.enum(['signed-out', 'unavailable']),
  }),
  // A push carries no `id`. Checked after the answers, so an answer is never
  // taken for one.
  z.object({ v: z.literal(1), type: z.literal('token'), access: z.string().min(1) }),
  z.object({ v: z.literal(1), type: z.literal('signed-out') }),
  z.object({ v: z.literal(1), type: z.literal('theme'), theme: themeSchema }),
])

type PageMessage =
  | { type: 'token'; id: string; reason: RenewReason }
  | { type: 'signed-out'; reason: 'expired' | 'rejected' }
  | { type: 'open'; path: string }

/** What the app may say unasked. Answers to `token` requests are the bridge's own. */
export interface AppHandlers {
  onToken: (access: string) => void
  onSignedOut: () => void
  onTheme: (theme: AppTheme) => void
}

/**
 * What the tab was opened with. Kept in sessionStorage so it outlives a reload:
 * the chart's own navigations rewrite the URL through its search schema, which
 * drops `embed` and `theme`, and a WebView reload then has neither.
 */
const KEPT_KEY = 'mm-embed'
const keptSchema = z.object({ theme: themeSchema.nullable() })

const readKept = (): z.infer<typeof keptSchema> | null => {
  try {
    const raw = sessionStorage.getItem(KEPT_KEY)
    return raw === null ? null : (keptSchema.safeParse(JSON.parse(raw)).data ?? null)
  } catch {
    return null
  }
}

const keep = (theme: AppTheme | null): void => {
  try {
    sessionStorage.setItem(KEPT_KEY, JSON.stringify({ theme }))
  } catch {
    // Non-fatal: this page view is embedded either way; a reload may not be.
  }
}

const isAppChannel = (value: unknown): value is AppChannel =>
  typeof value === 'object' &&
  value !== null &&
  typeof (value as { postMessage?: unknown }).postMessage === 'function'

interface Embedding {
  channel: AppChannel
  theme: AppTheme | null
}

/**
 * Embed mode needs both the URL's `?embed=1` (or a tab that had it) and the
 * app's channel. The flag alone is a link anyone can open in a browser, which
 * has no app to ask for a token; that must stay the normal web chart.
 *
 * index.html's second pre-paint script reads the same two things to pick the
 * first frame's theme. Keep them in step.
 */
const detect = (): Embedding | null => {
  const channel = window.MMApp
  if (!isAppChannel(channel)) return null
  const query = new URLSearchParams(window.location.search)
  const kept = readKept()
  if (query.get('embed') !== '1' && kept === null) return null
  const theme = themeSchema.safeParse(query.get('theme')).data ?? kept?.theme ?? null
  keep(theme)
  return { channel, theme }
}

/** Decided on first read and kept for the tab's life. `undefined`: not read yet. */
let embedding: Embedding | null | undefined

const current = (): Embedding | null => {
  if (embedding === undefined) embedding = detect()
  return embedding
}

/** Whether the page runs inside the Flutter app. Settled at boot, never changes after. */
export const isEmbedded = (): boolean => current() !== null

/** The theme the app opened the page with, or null when it named none (or there is no app). */
export const openingTheme = (): AppTheme | null => current()?.theme ?? null

/** False when there is no app to tell, or its channel threw. */
const post = (message: PageMessage): boolean => {
  const channel = current()?.channel
  if (!channel) return false
  try {
    // A method call on the channel object: the Android channel throws when its
    // function is called detached from it.
    channel.postMessage(JSON.stringify({ v: 1, ...message }))
    return true
  } catch {
    return false
  }
}

export const postSignedOut = (reason: 'expired' | 'rejected'): void => {
  post({ type: 'signed-out', reason })
}

export const postOpen = (path: string): void => {
  post({ type: 'open', path })
}

/** Contract rule 3: a `token` request with no answer by then counts as `unavailable`. */
const TOKEN_WAIT_MS = 10_000

/** Token requests waiting on the app, by id. */
const awaiting = new Map<string, (renewal: Renewal) => void>()

/** Asks the app for an access token. Never rejects; see `TOKEN_WAIT_MS`. */
export const requestToken = (reason: RenewReason): Promise<Renewal> =>
  new Promise((resolve) => {
    const id = newRequestId()
    const settle = (renewal: Renewal) => {
      clearTimeout(timer)
      awaiting.delete(id)
      resolve(renewal)
    }
    const timer = setTimeout(() => {
      settle({ error: 'unavailable' })
    }, TOKEN_WAIT_MS)
    awaiting.set(id, settle)
    if (!post({ type: 'token', id, reason })) settle({ error: 'unavailable' })
  })

/**
 * Defines `window.mmBridge.receive`, which the app calls with each message.
 * Must run before the page first posts: the app may answer at once.
 *
 * Anything that does not parse is dropped without a word in the console —
 * a malformed message may still carry a token.
 */
export const listen = (handlers: AppHandlers): void => {
  window.mmBridge = {
    receive: (raw) => {
      const parsed = appMessageSchema.safeParse(raw)
      if (!parsed.success) return
      const message = parsed.data
      switch (message.type) {
        case 'token':
          if (!('id' in message)) handlers.onToken(message.access)
          else {
            // An id with no request behind it (one already timed out) is dropped.
            awaiting.get(message.id)?.(
              'access' in message ? { access: message.access } : { error: message.error },
            )
          }
          return
        case 'signed-out':
          handlers.onSignedOut()
          return
        case 'theme':
          keep(message.theme)
          handlers.onTheme(message.theme)
      }
    },
  }
}
